import { Divider, Skeleton, SkeletonItem, TableCell, TableRow, mergeClasses } from '@fluentui/react-components';
import { useMemo } from 'react';
import { useSharedStyles } from '../shared-styles.js';
import type { FieldTableProps, FieldValueRecord } from '../types.js';
import { useStyles } from './styles.js';

type FieldTableTotalProps<TValue extends Array<FieldValueRecord>> = {
    fieldValue?: TValue;
    totalField: NonNullable<FieldTableProps<TValue>['totalField']>;
    totalRows: number;
};

export function FieldTableTotal<TValue extends Array<FieldValueRecord>>({
    fieldValue,
    totalField,
    totalRows,
}: FieldTableTotalProps<TValue>) {
    const styles = useStyles();
    const sharedStyles = useSharedStyles();

    const fieldValueTotal = useMemo(() => {
        if (!fieldValue) {
            return null;
        }

        return totalField?.serializer(
            fieldValue.reduce((acc, value) => {
                return acc + ((value[totalField.key as keyof FieldValueRecord] as number | null) ?? 0);
            }, 0)
        );
    }, [fieldValue, totalField]);

    return (
        <>
            <TableRow className={sharedStyles.tableDisplayContents}>
                <TableCell className={sharedStyles.tableDisplayContents}>
                    <Divider className={sharedStyles.tableDivider} />
                </TableCell>
            </TableRow>

            {totalRows ? (
                <TableRow className={sharedStyles.tableDisplayContents}>
                    <TableCell className={mergeClasses(sharedStyles.tableCell, styles.tableCellSpanAllMinusOne)}>
                        {totalField.label}
                    </TableCell>
                    <TableCell className={mergeClasses(sharedStyles.tableCell, sharedStyles.tableCellAlignRight)}>
                        {fieldValueTotal}
                    </TableCell>
                </TableRow>
            ) : (
                <TableRow className={sharedStyles.tableDisplayContents}>
                    <TableCell className={mergeClasses(sharedStyles.tableCell, styles.tableCellSpanAllMinusOne)} />
                    <TableCell className={sharedStyles.tableCell}>
                        <Skeleton className={sharedStyles.tableCellSkeleton}>
                            <SkeletonItem className={sharedStyles.tableCellSkeletonItem} />
                        </Skeleton>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
}
